/**
 * Google Maps Styling Configuration
 *
 * Minimalist "Slate & Steel" style matching the simcheck.ai brand identity.
 * Uses muted, monochromatic tones for a professional, tech-oriented aesthetic.
 */

// Ultra-light colors for minimalist map matching login background style
export const MAP_BRAND_COLORS = {
  primary: '#545A5F',      // Slate - Primary brand color (not used on map)
  secondary: '#75797C',    // Steel - Secondary hierarchy (not used on map)
  background: '#F8F9FA',   // Light background
  water: '#F0F3F5',        // Very light water - barely visible
  land: '#FFFFFF',         // Clean white land
  borders: '#E5E8EB',      // Very subtle borders
  bordersCountry: '#D8DCE0', // Slightly more visible country borders
  roads: '#F0F2F4',        // Nearly invisible roads
  roadsMain: '#E8EAED',    // Very subtle main roads
  labels: '#B8BCC2',       // Light gray labels
  labelsCountry: '#A0A5AB', // Slightly darker for country names
  labelsSecondary: '#C8CDD2', // Very light secondary labels
};

/**
 * Minimalist map style array for Google Maps
 * Creates a clean, monochromatic appearance that doesn't compete with data markers
 */
export const MINIMALIST_MAP_STYLES: google.maps.MapTypeStyle[] = [
  // Base geometry - clean white land
  {
    featureType: 'all',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.land }],
  },

  // Water - muted blue-gray
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.water }],
  },
  {
    featureType: 'water',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },

  // Land parcels
  {
    featureType: 'landscape',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.land }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.fill',
    stylers: [{ color: '#F4F6F8' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.land }],
  },

  // Administrative boundaries - very subtle
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: MAP_BRAND_COLORS.borders }, { weight: 0.5 }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: MAP_BRAND_COLORS.bordersCountry }, { weight: 0.7 }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: MAP_BRAND_COLORS.borders }, { weight: 0.3 }],
  },

  // Administrative labels - light and subtle
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: MAP_BRAND_COLORS.labelsCountry }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#FFFFFF' }, { weight: 3 }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'labels.text.fill',
    stylers: [{ color: MAP_BRAND_COLORS.labelsSecondary }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: MAP_BRAND_COLORS.labels }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#FFFFFF' }, { weight: 3 }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },

  // Roads - very subtle
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.roads }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.roadsMain }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.fill',
    stylers: [{ color: MAP_BRAND_COLORS.roads }],
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },

  // Hide POIs completely
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },

  // Hide transit
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
];

/**
 * Dark mode minimalist map style
 * For potential future dark mode support
 */
export const MINIMALIST_MAP_STYLES_DARK: google.maps.MapTypeStyle[] = [
  // Base - dark background
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#1A1D21' }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#75797C' }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1A1D21' }, { weight: 2 }],
  },

  // Water - dark muted
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [{ color: '#12151A' }],
  },
  {
    featureType: 'water',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },

  // Land
  {
    featureType: 'landscape',
    elementType: 'geometry.fill',
    stylers: [{ color: '#1A1D21' }],
  },

  // Administrative boundaries
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#2A2D32' }, { weight: 0.8 }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#3A3D42' }, { weight: 1 }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#545A5F' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#75797C' }],
  },

  // Roads - minimal visibility
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#252830' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },

  // Hide POIs
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }],
  },

  // Hide transit
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }],
  },
];

/**
 * Base map options shared across all map instances
 */
export const BASE_MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: true,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: true,
};

/**
 * Get map options with appropriate styling
 * Note: AdvancedMarkerElement requires mapId to work. When mapId is provided,
 * custom styles won't apply (cloud styling takes precedence).
 * Custom styles only work when mapId is NOT provided (uses deprecated Marker).
 */
export function getMapOptions(mapId?: string, isDarkMode = false): google.maps.MapOptions {
  // If mapId is provided, use it (required for AdvancedMarkerElement)
  // Custom styles don't work with mapId - would need to configure in Google Cloud Console
  if (mapId) {
    // colorScheme picks the Map ID's Light-mode or Dark-mode style slot
    // configured in Cloud Studio at runtime.
    return {
      ...BASE_MAP_OPTIONS,
      mapId,
      colorScheme: (isDarkMode ? 'DARK' : 'LIGHT') as google.maps.ColorScheme,
    };
  }

  // No mapId - use custom inline styles
  return {
    ...BASE_MAP_OPTIONS,
    styles: isDarkMode ? MINIMALIST_MAP_STYLES_DARK : MINIMALIST_MAP_STYLES,
  };
}
